import { useState } from "react";
import Button from "../../components/ui/Button";
import Modal from "../../components/ui/Modal";
import useProjectStore from "../../store/projectStore";
import { bladesApi } from "../../api/blades";
import useBladeReadonly from "../../hooks/useBladeReadonly";

export default function TransformPage() {
  const { activeDesign, setActiveDesign, saveConstructionLines } = useProjectStore();
  const { isReleased, isLockedByOther } = useBladeReadonly();
  const [doScale, setDoScale]         = useState(false);
  const [scaleFactor, setScaleFactor] = useState("1.0");
  const [doReverse, setDoReverse]     = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState(null);

  if (!activeDesign) return <div className="p-6 text-sm text-gray-500">No design loaded.</div>;
  if (isReleased) return (
    <div className="p-6 text-sm text-amber-600">
      This design is released and cannot be edited.
    </div>
  );
  if (isLockedByOther) return (
    <div className="p-6 text-sm text-orange-600">
      This design is checked out by {activeDesign.checked_out_by} — read-only until checked in.
    </div>
  );

  const canApply = doScale || doReverse;
  const f = parseFloat(scaleFactor);
  const scaleValid = doScale ? (!isNaN(f) && f > 0) : true;

  // All mm/length fields in assistant params objects — angles (alpha_le, alpha_te, gamma4, gamma7) are excluded
  const MECO_LEN_FIELDS = ["le_r", "le_z", "te_r", "te_z", "inlet_z_start", "l1", "l2", "l_out"];
  const DIFF_LEN_FIELDS = ["R3", "Z3", "R4", "R5", "Z5", "Z5b", "R6", "Z6", "R7", "R8", "Z8", "L9"];
  const scaleParamObj = (p, factor) => {
    const out = { ...p };
    [...MECO_LEN_FIELDS, ...DIFF_LEN_FIELDS].forEach(k => { if (out[k] != null) out[k] *= factor; });
    return out;
  };

  const handleApply = async () => {
    setShowConfirm(false);
    setSaving(true);
    setError(null);
    try {
      let cls = (activeDesign.construction_lines || []).map(cl => {
        let out = { ...cl };

        if (doScale) {
          if (cl.r_coords?.length) out.r_coords = cl.r_coords.map(v => v * f);
          if (cl.z_coords?.length) out.z_coords = cl.z_coords.map(v => v * f);
          if (cl.le_r != null)     out.le_r = cl.le_r * f;
          if (cl.le_z != null)     out.le_z = cl.le_z * f;
          if (cl.te_r != null)     out.te_r = cl.te_r * f;
          if (cl.te_z != null)     out.te_z = cl.te_z * f;

          if (cl.meco_spline_params) {
            out.meco_spline_params = scaleParamObj(cl.meco_spline_params, f);
          }

          if (cl.thickness_spline?.control_points?.length) {
            out.thickness_spline = {
              ...cl.thickness_spline,
              control_points: cl.thickness_spline.control_points.map(([s, t]) => [s, t * f]),
            };
          }
        }

        if (doReverse) {
          if (cl.beta_spline?.control_points?.length) {
            out.beta_spline = {
              ...cl.beta_spline,
              control_points: cl.beta_spline.control_points.map(([m, b]) => [m, -b]),
            };
          }
          if (cl.theta_start != null) out.theta_start = -cl.theta_start;
        }

        return out;
      });

      await saveConstructionLines(activeDesign.id, cls);

      if (doScale) {
        const updates = {};

        // Scale blade-level B-spline assistant params (hub + shroud sub-objects)
        if (activeDesign.meco_assistant_params) {
          const ap = activeDesign.meco_assistant_params;
          updates.meco_assistant_params = {
            ...ap,
            hub:    ap.hub    ? scaleParamObj(ap.hub,    f) : ap.hub,
            shroud: ap.shroud ? scaleParamObj(ap.shroud, f) : ap.shroud,
          };
        }

        // Scale cut-off line coordinates
        if (activeDesign.cutoff_active && activeDesign.cutoff_r?.length) {
          updates.cutoff_r = activeDesign.cutoff_r.map(v => v * f);
          updates.cutoff_z = (activeDesign.cutoff_z || []).map(v => v * f);
        }

        if (Object.keys(updates).length) {
          const { data: updated } = await bladesApi.update(activeDesign.id, updates);
          setActiveDesign(updated);
        }
      }
    } catch (e) {
      setError(e?.response?.data?.error || "Transform failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-lg space-y-5">
      <div>
        <h2 className="text-base font-semibold text-gray-800">Transform</h2>
        <p className="text-xs text-gray-400 mt-0.5">
          Destructive — modifies stored data permanently. Cannot be undone.
        </p>
      </div>

      {/* Scale */}
      <div className={`rounded-lg border p-4 transition-colors
        ${doScale ? "border-bh-300 bg-bh-50/30" : "border-gray-200 bg-white"}`}>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox" checked={doScale} onChange={e => setDoScale(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-bh-600 cursor-pointer shrink-0"
          />
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-800">Scale</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Multiplies all MECO coordinates, edge points, thickness values, and cut-off line by a uniform factor.
            </p>
            {doScale && (
              <div className="mt-3 flex items-center gap-2">
                <label className="text-xs font-medium text-gray-600">Factor</label>
                <input
                  type="number" step="0.01" min="0.001"
                  value={scaleFactor}
                  onChange={e => setScaleFactor(e.target.value)}
                  className={`w-24 border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-bh-500
                    ${!scaleValid ? "border-red-400" : "border-gray-300"}`}
                  autoFocus
                />
                {scaleValid && (
                  <span className="text-xs text-gray-400">all lengths × {f}</span>
                )}
                {!scaleValid && (
                  <span className="text-xs text-red-500">Must be &gt; 0</span>
                )}
              </div>
            )}
          </div>
        </label>
      </div>

      {/* Reverse rotation */}
      <div className={`rounded-lg border p-4 transition-colors
        ${doReverse ? "border-bh-300 bg-bh-50/30" : "border-gray-200 bg-white"}`}>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox" checked={doReverse} onChange={e => setDoReverse(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-bh-600 cursor-pointer shrink-0"
          />
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-800">Reverse rotation</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Negates all beta angles and initial wrap angles — converts CW ↔ CCW rotation direction.
            </p>
          </div>
        </label>
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      <Button
        onClick={() => setShowConfirm(true)}
        disabled={!canApply || !scaleValid || saving}
        loading={saving}
        className="w-full justify-center"
      >
        Apply transform
      </Button>

      <Modal open={showConfirm} onClose={() => setShowConfirm(false)} title="Apply transform?">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            This will permanently modify the stored design data. The operation cannot be undone.
          </p>
          <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
            {doScale && (
              <li>Scale all lengths by <span className="font-semibold">×{f}</span></li>
            )}
            {doReverse && (
              <li>Negate all beta angles (reverse rotation direction)</li>
            )}
          </ul>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => setShowConfirm(false)}>Cancel</Button>
            <Button onClick={handleApply}>Yes, apply</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
